"use client";
import React, { useState } from "react";

import { config, type Currency } from "@/components/forms/stripe-donation-form-config";

interface DonationFormProps {
  currency?: Currency;
}

const DonationForm: React.FC<DonationFormProps> = ({ currency = "USD" }) => {
  const [selectedFrequency, setSelectedFrequency] = useState(config.defaultDonationFrequency);
  const [selectedAmount, setSelectedAmount] = useState(config.defaultDonationAmount);
  const [amountIsCompatibleWithFrequency, setAmountIsCompatibleWithFrequency] = useState(true);

  const currencySymbols: { [key in Currency]: string } = {
    GBP: "£",
    USD: "$",
  };

  const getCurrencySymbol = (curr: Currency): string => {
    return currencySymbols[curr];
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;

    const handlers: { [key: string]: () => void } = {
      "donation-amount": () => setSelectedAmount(value),
      "donation-frequency": () => {
        setSelectedFrequency(value as "one-time" | "monthly");
        setAmountIsCompatibleWithFrequency(value !== "monthly");
      },
    };

    const handler = handlers[name];
    if (handler) {
      handler();
    }
  };

  const handleSubmit = (): void => {
    const donationLinks =
      config.paymentEnvironment === "test" ? config.testDonationLinks : config.liveDonationLinks;

    const selectedLink = donationLinks.find(
      (link) => link.frequency === selectedFrequency && link.amount === selectedAmount,
    );

    // If no link is found and we're not looking for an "other" amount
    if (!selectedLink && selectedAmount !== "other") {
      console.error("No matching donation link found for the selected amount and frequency.");
      return;
    }

    // If we're looking for an "other" amount, find the appropriate "other" link
    if (selectedAmount === "other") {
      const otherLink = donationLinks.find(
        (link) => link.frequency === selectedFrequency && link.amount === "other",
      );

      if (otherLink) {
        window.open(otherLink.hrefValuesByCurrency[currency], "_blank");
        return;
      }
    }

    // If we found a matching link for a specific amount
    if (selectedLink) {
      window.open(selectedLink.hrefValuesByCurrency[currency], "_blank");
    } else {
      console.error("No matching donation link found.");
    }
  };

  return (
    <>
      <form
        className="donation-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <legend className="hide">Donate</legend>
        <div className="donation-frequency-and-donation-amounts">
          <div className="donation-frequency">
            <ul className="donation-form-options donation-frequency-options">
              <li
                className={`donation-frequency-option donation-frequency-option-one-time ${selectedFrequency === "one-time" ? "is-checked" : ""}`}
              >
                <label className={selectedFrequency === "one-time" ? "is-checked" : ""}>
                  <input
                    checked={selectedFrequency === "one-time"}
                    name="donation-frequency"
                    type="radio"
                    value="one-time"
                    onChange={handleRadioChange}
                  />
                  One-time
                </label>
              </li>
              <li
                className={`donation-frequency-option donation-frequency-option-recurring ${selectedFrequency === "monthly" ? "is-checked" : ""}`}
              >
                <label className={selectedFrequency === "monthly" ? "is-checked" : ""}>
                  <input
                    checked={selectedFrequency === "monthly"}
                    name="donation-frequency"
                    type="radio"
                    value="monthly"
                    onChange={handleRadioChange}
                  />
                  Monthly
                </label>
              </li>
            </ul>
          </div>

          <div className="donation-amounts">
            <label className="hide" htmlFor="donation-amount">
              Donation Amount:
            </label>
            <ul className="donation-form-options donation-amount-options">
              {[25, 100, 200, 500].map((amount) => (
                <li
                  className={`donation-amount-option${selectedAmount === amount.toString() ? " is-checked" : ""}`}
                  key={amount}
                >
                  <label className={selectedAmount === amount.toString() ? "is-checked" : ""}>
                    <input
                      checked={selectedAmount === amount.toString()}
                      name="donation-amount"
                      type="radio"
                      value={amount.toString()}
                      onChange={handleRadioChange}
                    />
                    {getCurrencySymbol(currency)}
                    {amount}
                  </label>
                </li>
              ))}
              <li
                className={`donation-amount-option donation-amount-option-other${selectedAmount === "other" ? " is-checked" : ""}`}
                data-donation-amount-is-compatible-with-donation-frequency={
                  amountIsCompatibleWithFrequency
                }
              >
                <label className={selectedAmount === "other" ? "is-checked" : ""}>
                  <input
                    checked={selectedAmount === "other"}
                    name="donation-amount"
                    type="radio"
                    value="other"
                    onChange={handleRadioChange}
                  />
                  {getCurrencySymbol(currency)}Other Amount
                </label>
              </li>
            </ul>
          </div>
        </div>
        <div className="donation-form-submit-and-confirmation-display">
          <div className="donation-form-submit-and-confirmation-display-cell">
            <button className="enhance-press" type="submit">
              Donate Now
            </button>
          </div>
          <div className="donation-form-submit-and-confirmation-display-cell">
            <dl className="donation-frequency-and-amount-confirmation">
              <dt>Donation Frequency:</dt>
              <dd className="donation-frequency">
                {selectedFrequency === "one-time" ? "One-time donation" : "Monthly donation"}
              </dd>
              <dt>Donation Amount:</dt>
              <dd className="donation-amount">
                {selectedFrequency === "monthly" && selectedAmount === "other"
                  ? "Please select an amount above"
                  : selectedAmount === "other"
                    ? `${getCurrencySymbol(currency)}Other`
                    : `${getCurrencySymbol(currency)}${selectedAmount}`}
              </dd>
            </dl>
          </div>
        </div>
      </form>
    </>
  );
};

export default DonationForm;
